import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Review, ReviewQuestionItem } from '../../types';
import DashboardHeader from '../dashboard/DashboardHeader';
import Footer from '../dashboard/Footer';
import RatingCheckbox from './RatingCheckbox';
import { formatRating, roundRating } from '../../utils/rating';
import './ReviewDetail.css';
import './PublicReviewForm.css';

const ReviewEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [review, setReview] = useState<Review | null>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    rating: 10,
    comment: '',
    status: 'pending' as string,
    show_on_site: false,
  });
  /** Question ratings: question_id -> rating (1-10). When present, main rating is calculated as average. */
  const [questionAnswers, setQuestionAnswers] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    const fetchReview = async () => {
      try {
        setLoading(true);
        const response = await api.get<Review>(`/reviews/${id}`);
        const r = response.data;
        setReview(r);
        setForm({
          first_name: r.first_name || '',
          last_name: r.last_name || '',
          email: r.email || '',
          phone: r.phone || '',
          rating: r.rating ?? 10,
          comment: r.comment || '',
          status: r.status || 'pending',
          show_on_site: r.show_on_site ?? false,
        });
        const answers: { [key: number]: number } = {};
        if (r.questions && r.questions.length > 0) {
          r.questions.forEach((q: ReviewQuestionItem) => {
            answers[q.id] = q.pivot?.rating ?? 0;
          });
        }
        setQuestionAnswers(answers);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load review');
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [id]);

  /** Calculate main rating from question ratings (same as public form). */
  const calculateRating = (): number => {
    const answers = Object.values(questionAnswers).filter((v) => v > 0);
    if (answers.length > 0) {
      const sum = answers.reduce((acc, val) => acc + val, 0);
      return sum / answers.length;
    }
    return form.rating;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const calculated = calculateRating();
      const rating = roundRating(
        review?.questions?.length && calculated > 0 ? calculated : form.rating
      );
      const questionAnswersArray = Object.entries(questionAnswers)
        .filter(([_, r]) => r > 0)
        .map(([questionId, ratingVal]) => ({
          question_id: parseInt(questionId, 10),
          rating: ratingVal,
        }));

      const payload: any = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        rating,
        comment: form.comment,
        status: form.status,
        show_on_site: form.show_on_site,
      };
      if (questionAnswersArray.length > 0) {
        payload.question_answers = questionAnswersArray;
      }

      await api.put(`/reviews/${id}`, payload);
      navigate(`/dashboard/reviews/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update review');
    } finally {
      setSaving(false);
    }
  };

  const hasQuestions = review?.questions && review.questions.length > 0;
  const calculatedRating = hasQuestions ? calculateRating() : form.rating;

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader title="Edit Review" showNav={false} showEmailButton={false} />
        <div className="dashboard-content">
          <div className="detail-container">
            <div className="loading">Loading review...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !review) {
    return (
      <div className="dashboard">
        <DashboardHeader title="Edit Review" showNav={false} showEmailButton={false} />
        <div className="dashboard-content">
          <div className="detail-container">
            <div className="error-message">{error}</div>
            <button onClick={() => navigate('/dashboard?tab=reviews')} className="back-button">
              ← Back to Reviews
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader title="Edit Review" showNav={false} showEmailButton={false} />
      <div className="dashboard-content">
        <div className="detail-container">
          <div className="detail-header">
            <button onClick={() => navigate(`/dashboard/reviews/${id}`)} className="back-button">
              ← Back to Review
            </button>
            <h1>Edit Review</h1>
          </div>

          <div className="public-review-card review-edit-card">
            <p className="subtitle">Edit the review details below. Main rating is calculated from question ratings.</p>

            <form onSubmit={handleSubmit} className="review-form">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-group">
                <label htmlFor="first_name">First Name *</label>
                <input
                  id="first_name"
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  required
                  placeholder="Enter first name"
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">Last Name *</label>
                <input
                  id="last_name"
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  required
                  placeholder="Enter last name"
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  placeholder="your.email@example.com"
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="999-999-9999"
                  maxLength={255}
                />
              </div>

              {/* Question ratings (same as public form) */}
              {hasQuestions ? (
                <>
                  {review!.questions!.map((q: ReviewQuestionItem) => (
                    <RatingCheckbox
                      key={q.id}
                      label={q.question_title}
                      value={questionAnswers[q.id] || 0}
                      onChange={(value) => {
                        setQuestionAnswers((prev) => ({ ...prev, [q.id]: value }));
                      }}
                    />
                  ))}
                  <div className="form-group calculated-rating">
                    <label>Calculated overall rating</label>
                    <p className="rating-value">{formatRating(calculatedRating)} / 10</p>
                    <small>Average of question ratings above (saved when you click Save).</small>
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label htmlFor="rating">Overall Rating (1–10) *</label>
                  <input
                    id="rating"
                    type="number"
                    min={1}
                    max={10}
                    step={0.1}
                    value={form.rating}
                    onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) || 10 }))}
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="comment">Comment *</label>
                <textarea
                  id="comment"
                  value={form.comment}
                  onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                  required
                  placeholder="Share your experience"
                  maxLength={2000}
                  rows={5}
                />
                <div className="char-counter">{form.comment.length} / 2000 characters</div>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.show_on_site}
                    onChange={(e) => setForm((f) => ({ ...f, show_on_site: e.target.checked }))}
                  />
                  <span>Show on Site</span>
                </label>
              </div>

              <div className="review-edit-actions form-actions">
                <button type="submit" className="submit-button" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary cancel-edit-btn"
                  onClick={() => navigate(`/dashboard/reviews/${id}`)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReviewEdit;
